import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import SimplePageHero from '../marketing/components/SimplePageHero';
import { marketingColors as C, marketingFonts as F } from '../theme_landing_page';

const lists = [
  { title: 'New tenancy setup', items: ['Right to Rent checks completed', 'Deposit protected within 30 days', 'Prescribed Information served', 'How to Rent guide issued', 'Gas Safety, EPC & EICR given to tenant', 'Inventory and check-in report signed'] },
  { title: 'Annual compliance', items: ['Gas Safety Certificate (CP12) renewed', 'Smoke & CO alarms tested', 'EICR remedials actioned', 'EPC still valid (min band E)', 'Landlord insurance renewed', 'Licence conditions reviewed'] },
  { title: 'HMO licensing', items: ['HMO licence valid & displayed', 'Fire Risk Assessment current', 'Emergency lighting tested', 'Fire doors & closers checked', 'PAT testing completed', 'Room sizes meet licence conditions'] },
  { title: 'End of tenancy', items: ['Check-out inventory completed', 'Deposit return / deductions agreed', 'Final meter readings taken', 'Property cleaned & certificates refreshed', 'Forwarding address recorded', 'Void period costs logged'] },
];

const ChecklistsPage: React.FC = () => {
  useMarketingFonts();
  useSeoHead({
    title: 'UK Landlord Compliance Checklists | MillOS',
    description: 'Step-by-step UK landlord checklists: new tenancy setup, annual compliance, HMO licensing and end-of-tenancy. Tick each item or let MillOS automate it.',
    canonicalPath: '/checklists',
  });

  return (
    <Box className="lv-marketing" sx={{ background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'hidden' }}>
      <MarketingNav />
      <SimplePageHero
        eyebrow="Checklists"
        title={<>Landlord <Box component="em" sx={{ fontStyle: 'italic' }}>Checklists</Box></>}
        body="Every step, every stage. Tick them off yourself — or let MillOS run them automatically."
      />
      <Box component="section" sx={{ padding: { xs: '48px 16px 64px', md: '56px 52px 96px' } }}>
        <Box sx={{ maxWidth: 1000, margin: '0 auto', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '22px' }}>
          {lists.map((l) => (
            <Box key={l.title} sx={{ background: C.panel, borderRadius: '24px', padding: '32px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
                <Box sx={{ width: 46, height: 46, borderRadius: '13px', background: C.accent, display: 'grid', placeItems: 'center', flex: 'none' }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="m9 11 3 3L22 4" />
                    <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
                  </svg>
                </Box>
                <Box component="h2" sx={{ fontFamily: F.display, fontSize: 26, lineHeight: 1.05, margin: 0 }}>{l.title}</Box>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {l.items.map((it) => (
                  <Box key={it} sx={{ display: 'flex', gap: '12px', alignItems: 'flex-start', fontSize: 15, color: C.proseText, lineHeight: 1.5 }}>
                    <Box sx={{ marginTop: '2px', width: 20, height: 20, borderRadius: '6px', border: `1.5px solid ${C.accent}`, display: 'grid', placeItems: 'center', flex: 'none' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                        <path d="m5 12 5 5L20 7" />
                      </svg>
                    </Box>
                    {it}
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            maxWidth: 1000,
            margin: '32px auto 0',
            background: C.heroGradient,
            borderRadius: '24px',
            padding: { xs: '40px 24px', md: '48px 40px' },
            textAlign: 'center',
            color: C.white,
          }}
        >
          <Box component="h2" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 'clamp(1.8rem,3.4vw,2.6rem)', letterSpacing: '-0.02em', color: C.white }}>
            Stop ticking boxes by hand
          </Box>
          <Box sx={{ maxWidth: '34rem', margin: '14px auto 0', color: 'rgba(255,255,255,0.8)', fontSize: 16 }}>
            MillOS runs every checklist automatically — booking, chasing and filing so nothing slips.
          </Box>
          <Box
            component={RouterLink}
            to="/register"
            sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: C.white, color: C.ink, borderRadius: '32px', padding: '13px 26px', fontSize: 16, marginTop: '24px', '&:hover': { background: C.accent, color: C.white } }}
          >
            Start free trial
          </Box>
        </Box>
      </Box>
      <MarketingFooter />
    </Box>
  );
};

export default ChecklistsPage;
